/**
 * Signup attribution capture (GTM plan F1/F2).
 *
 * vardic.eu appends the campaign parameters to every app link
 * (landing/src/lib/attribution.ts). This module grabs them on the first page
 * load and keeps them until the account is created, because the signup flow
 * crosses Firebase: email confirmation can land in a fresh tab, and the
 * parameters are gone from `location.search` once the app navigates.
 *
 * Storage is localStorage (not sessionStorage) for exactly that fresh-tab
 * case. Entries expire after ATTRIBUTION_TTL_MS: a campaign from a month ago
 * must not be credited to a signup that starts today.
 */

import { z } from "zod";

const STORAGE_KEY = "vardic.signup.attribution";
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Campaign parameters the landing forwards. Kept in sync with the backend's
 * registerSchema.signupAttribution. */
const CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "ref",
] as const;

const attributionSchema = z.object({
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
  gclid: z.string().optional(),
  ref: z.string().optional(),
  landing_page: z.string().optional(),
  referrer: z.string().optional(),
  captured_at: z.number(),
});

export type SignupAttribution = z.infer<typeof attributionSchema>;

/**
 * Attribution implied by a page URL and referrer. Returns null when the URL
 * carries no campaign parameter — a direct visit is not a campaign, and
 * storing it would overwrite a real earlier touch.
 */
export function readAttribution(
  search: string,
  referrer: string,
  landingPath: string,
  now: number,
): SignupAttribution | null {
  const params = new URLSearchParams(search);
  const attribution: SignupAttribution = { captured_at: now };
  let found = false;
  for (const key of CAMPAIGN_PARAMS) {
    const value = params.get(key);
    if (value) {
      attribution[key] = value;
      found = true;
    }
  }
  if (!found) return null;
  attribution.landing_page = landingPath;
  if (referrer) attribution.referrer = referrer;
  return attribution;
}

function isFresh(value: SignupAttribution, now: number): boolean {
  return (
    now - value.captured_at < ATTRIBUTION_TTL_MS && now >= value.captured_at
  );
}

/**
 * Persist the current page's campaign parameters when it has any, and return
 * what is stored afterwards. Safe to call on every boot: a visit without
 * campaign parameters leaves the previous entry untouched.
 */
export function captureSignupAttribution(
  storage: Storage,
  now: number = Date.now(),
): SignupAttribution | null {
  try {
    const current = readAttribution(
      window.location.search,
      document.referrer,
      window.location.pathname,
      now,
    );
    if (current) {
      storage.setItem(STORAGE_KEY, JSON.stringify(current));
      return current;
    }
    return readStoredAttribution(storage, now);
  } catch {
    // Private-mode storage denial must never break signup.
    return null;
  }
}

export function readStoredAttribution(
  storage: Storage,
  now: number = Date.now(),
): SignupAttribution | null {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = attributionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || !isFresh(parsed.data, now)) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function clearStoredAttribution(storage: Storage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

/** Stored attribution in the shape the register endpoint accepts. */
export function signupAttributionForRequest(
  now: number = Date.now(),
): Record<string, string> | undefined {
  const stored = readStoredAttribution(window.localStorage, now);
  if (!stored) return undefined;
  const { captured_at: _capturedAt, ...properties } = stored;
  const entries = Object.entries(properties).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string",
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}
